/**
 * MindMapPage - Hub for all mind maps
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Plus,
  Brain,
  MapPin,
  Clock,
  Trash2,
  MoreVertical,
  Star,
  Search,
  ArrowLeft,
  Home,
} from 'lucide-react';
import { format } from 'date-fns';
import { useMapStore } from '../stores/mapStore';

const MindMapPage = () => {
  const navigate = useNavigate();
  const { maps, isLoadingMaps, loadMaps, createMap, deleteMap, user } = useMapStore();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newMapTitle, setNewMapTitle] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [menuOpenId, setMenuOpenId] = useState(null);

  useEffect(() => {
    loadMaps();
  }, [loadMaps]);

  const handleCreateMap = async () => {
    if (!newMapTitle.trim()) return;

    const map = await createMap(newMapTitle.trim(), maps.length === 0);
    if (map) {
      setShowCreateModal(false);
      setNewMapTitle('');
      navigate(`/mindmap/${map.id}`);
    }
  };

  const handleDeleteMap = async (mapId, e) => {
    e.stopPropagation();
    setMenuOpenId(null);
    if (window.confirm('Are you sure you want to delete this map?')) {
      await deleteMap(mapId);
    }
  };

  const filteredMaps = maps.filter(map =>
    map.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const mainMap = maps.find(m => m.is_main);
  const otherMaps = filteredMaps.filter(m => !m.is_main);

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Home size={20} className="text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">
                MindMap
              </h1>
              <p className="text-sm sm:text-base text-gray-400">
                Organize, connect, and explore your thoughts with AI
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors"
          >
            <Plus size={18} />
            <span>New Map</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-6xl mx-auto mb-6">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search maps..."
            className="w-full pl-12 pr-4 py-3 bg-gray-900 border border-gray-800 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 transition-colors"
          />
        </div>
      </div>

      {/* Main Map */}
      {mainMap && (
        <div className="max-w-6xl mx-auto mb-6 sm:mb-8">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3 sm:mb-4">
            Main Brain
          </h2>
          <motion.div
            whileHover={{ scale: 1.01 }}
            onClick={() => navigate(`/mindmap/${mainMap.id}`)}
            className="relative p-4 sm:p-6 bg-gradient-to-br from-indigo-600/20 to-purple-600/20 border border-indigo-500/30 rounded-xl sm:rounded-2xl cursor-pointer group"
          >
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-0">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-indigo-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Brain size={24} className="text-indigo-400 sm:hidden" />
                  <Brain size={28} className="text-indigo-400 hidden sm:block" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg sm:text-xl font-semibold text-white">
                      {mainMap.title}
                    </h3>
                    <Star size={14} className="text-yellow-400 fill-yellow-400" />
                  </div>
                  <p className="text-gray-400 text-xs sm:text-sm">
                    Your primary knowledge base
                  </p>
                </div>
              </div>

              <div className="text-left sm:text-right text-xs sm:text-sm text-gray-500 ml-15 sm:ml-0">
                <p>Updated {format(new Date(mainMap.updated_at), 'MMM d, yyyy')}</p>
              </div>
            </div>

            <div className="absolute inset-0 border-2 border-indigo-400/0 group-hover:border-indigo-400/50 rounded-xl sm:rounded-2xl transition-colors" />
          </motion.div>
        </div>
      )}

      {/* Other Maps */}
      <div className="max-w-6xl mx-auto">
        <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
          Sub-Maps
        </h2>

        {isLoadingMaps ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="h-40 bg-gray-900 rounded-xl animate-pulse"
              />
            ))}
          </div>
        ) : otherMaps.length === 0 && !mainMap ? (
          <div className="text-center py-16 bg-gray-900/50 rounded-2xl border border-gray-800">
            <Brain size={48} className="mx-auto text-gray-700 mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              No maps yet
            </h3>
            <p className="text-gray-600 mb-4">
              Create your first map to start building your second brain
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition-colors"
            >
              Create Your First Map
            </button>
          </div>
        ) : otherMaps.length === 0 ? (
          <div className="text-center py-16 bg-gray-900/50 rounded-2xl border border-gray-800">
            <MapPin size={48} className="mx-auto text-gray-700 mb-4" />
            <h3 className="text-lg font-medium text-gray-400 mb-2">
              No sub-maps yet
            </h3>
            <p className="text-gray-600 mb-4">
              Create focused maps for specific topics or projects
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-gray-800 hover:bg-gray-750 text-white rounded-lg transition-colors"
            >
              Create Sub-Map
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {otherMaps.map((map) => (
              <motion.div
                key={map.id}
                whileHover={{ scale: 1.02 }}
                onClick={() => navigate(`/mindmap/${map.id}`)}
                className="relative p-5 bg-gray-900 border border-gray-800 rounded-xl cursor-pointer group hover:border-gray-700 transition-colors"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
                    <MapPin size={20} className="text-gray-400" />
                  </div>

                  <div className="relative">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setMenuOpenId(menuOpenId === map.id ? null : map.id);
                      }}
                      className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gray-800 transition-all"
                    >
                      <MoreVertical size={16} className="text-gray-500" />
                    </button>

                    {menuOpenId === map.id && (
                      <div className="absolute right-0 top-8 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-10 py-1 min-w-[120px]">
                        <button
                          onClick={(e) => handleDeleteMap(map.id, e)}
                          className="w-full px-3 py-2 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                <h3 className="text-lg font-medium text-white mb-1">
                  {map.title}
                </h3>

                <div className="flex items-center gap-3 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock size={12} />
                    <span>{format(new Date(map.updated_at), 'MMM d')}</span>
                  </div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500/50 to-purple-500/50 rounded-b-xl opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            ))}

            {/* Create new map card */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              onClick={() => setShowCreateModal(true)}
              className="flex flex-col items-center justify-center p-5 h-full min-h-[160px] bg-gray-900/50 border-2 border-dashed border-gray-800 rounded-xl hover:border-gray-700 transition-colors"
            >
              <Plus size={24} className="text-gray-600 mb-2" />
              <span className="text-gray-500">New Sub-Map</span>
            </motion.button>
          </div>
        )}
      </div>

      {/* Create Map Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowCreateModal(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-gray-900 border border-gray-800 rounded-xl p-6"
          >
            <h2 className="text-xl font-semibold text-white mb-4">
              Create New Map
            </h2>

            <input
              type="text"
              value={newMapTitle}
              onChange={(e) => setNewMapTitle(e.target.value)}
              placeholder="Enter map name..."
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleCreateMap();
              }}
            />

            <div className="flex gap-3">
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-750 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateMap}
                disabled={!newMapTitle.trim()}
                className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Create
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default MindMapPage;
